import React, { useEffect, useState } from "react";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { get, defaultTo } from "lodash";
import { useFormik } from "formik";
import { Collapse, Col, Row, Typography, Modal, Spin, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../components/functional_components/Button/PFButton";
import DocumentsArchive from "./documentsArchive";
import WorkPackage from "./WorkPackage";
import { trimData, checkRoles, showError } from "../../../../utils/utilities";
import { projectEstimateTabs } from "../../../../utils/properties";
import ProjectEstimate from "../../../../services/api/ProjectEstimate";
import ProjectEstimateStatus from "../../../../services/api/ProjectEstimateStatus";
import Currency from "../../../../services/api/Currency";
import Vertical from "../../../../services/api/Vertical";
import { setLoading } from "../../../../redux/ducks/Loading";
const { Text } = Typography;

export default function PreventiveForm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const [state, setState] = useState({
    projectDataObj: [],
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    isEdit: false,
  });
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isEdit: checkRoles(userInfo.roles, projectEstimateTabs["preventiveForm"].edit),
    }));
    projectEstimateDataObj(params.id);
  }, []);

  const projectEstimateDataObj = async (projectId) => {
    dispatch(setLoading(true));
    const projectData = await ProjectEstimate.projectEstimateData(projectId);
    if (!projectData.error && projectData.data) {
      setState((prevState) => ({
        ...prevState,
        projectDataObj: get(projectData, "data", {}),
      }));
    } else {
      showError(get(projectData, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const getStatusList = async () => {
    const statuses = await ProjectEstimateStatus.getStatusesList("allPages=true");
    const optionList = [];
    if (!statuses.error && statuses.data) {
      statuses.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        statusList: optionList,
      }));
    } else {
      showError(get(statuses, "error.messageCode", "ER000"));
    }
  };

  const getCurrenciesList = async () => {
    const currencies = await Currency.currencies("allPages=true");
    const optionList = [];
    if (!currencies.error && currencies.data) {
      currencies.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        currenciesList: optionList,
      }));
    } else {
      showError(get(currencies, "error.messageCode", "ER000"));
    }
  };

  const getVerticalsList = async () => {
    const verticals = await Vertical.getVerticalList("allPages=true");
    const optionList = [];
    if (!verticals.error && verticals.data) {
      verticals.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        verticalsList: optionList,
      }));
    } else {
      showError(get(verticals, "error.messageCode", "ER000"));
    }
  };

  const closeModal = () => {
    formikPreventiveForm.resetForm();
    setState((prevState) => ({
      ...prevState,
      modal: { visible: false, type: "", data: {} },
    }));
  };

  const editPreventiveForm = () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    getStatusList();
    getCurrenciesList();
    getVerticalsList();
    setState((prevState) => ({
      ...prevState,
      loading: false,
      modal: { visible: true, type: "new" },
    }));
  };

  const formikPreventiveForm = useFormik({
    initialValues: {
      verticalId: get(state.projectDataObj, "verticalId", ""),
      projectEstimateStatusId: get(state.projectDataObj, "projectEstimateStatusId", ""),
      description: get(state.projectDataObj, "description", ""),
      currencyId: get(state.projectDataObj, "currencyId", ""),
      clientId: null,
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.verticalId) {
        errors.vertical = "Required!";
      }
      if (!values.projectEstimateStatusId) {
        errors.status = "Required!";
      }
      if (!values.description) {
        errors.description = "Required!";
      }
      if (!values.currencyId) {
        errors.currency = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      let data = {
        id: params.id,
        verticalId: values.verticalId,
        projectEstimateStatusId: values.projectEstimateStatusId,
        description: values.description,
        currencyId: values.currencyId,
      };
      const response = await ProjectEstimate.saveNewProjectEstimate(data);
      if (!response.error && response.data) {
        projectEstimateDataObj(params.id);
        closeModal();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const items = [
    {
      key: "1",
      label: "Work Package",
      children: <WorkPackage />,
      className: "mb-1em",
    },
    {
      key: "2",
      label: t("projectEstimate.detail.tabs.preventiveForm.documentsArchive"),
      children: <DocumentsArchive />,
    },
  ];

  return (
    <>
      <div>
        <Row className="mb-1em mt-1em hidden-small">
          <Col span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.legalEntity")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.legalEntityName, "-")}</div>
          </Col>

          <Col offset={4} span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.vertical")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.verticalId, "-")}</div>
          </Col>
        </Row>

        <Row className="mb-1em hidden-small">
          <Col span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.preventiveId")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.id, "-")}</div>
          </Col>

          <Col offset={4} span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.status")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.projectEstimateStatusId, "-")}</div>
          </Col>
        </Row>

        <Row className="mb-1em hidden-small">
          <Col span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.pfpClient")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.clientName, "-")}</div>
          </Col>

          <Col offset={4} span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.currency")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.currencyId, "-")}</div>
          </Col>
        </Row>

        <Row className="mb-1em hidden-small">
          <Col span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.projectIdPfp")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.projectId, "-")}</div>
          </Col>

          <Col offset={4} span={4}>
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.description")}</Text>
          </Col>
          <Col span={6} className="right">
            <div className="boxValue">{defaultTo(state.projectDataObj.description, "-")}</div>
          </Col>
        </Row>

        {/** View for small screens */}
        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.legalEntity")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.legalEntityName, "-")}</div>
          </Col>
        </Row>

        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.vertical")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.verticalId, "-")}</div>
          </Col>
        </Row>

        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.preventiveId")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.id, "-")}</div>
          </Col>
        </Row>

        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.status")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.projectEstimateStatusId, "-")}</div>
          </Col>
        </Row>

        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.pfpClient")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.clientName, "-")}</div>
          </Col>
        </Row>

        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.currency")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.currencyId, "-")}</div>
          </Col>
        </Row>
        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.projectIdPfp")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.projectId, "-")}</div>
          </Col>
        </Row>
        <Row className="hidden-large mb-1em">
          <Col span={24} className="mb-1em">
            <Text>{t("projectEstimate.detail.tabs.preventiveForm.description")}</Text>
          </Col>
          <Col span={24} className="mb-1em right">
            <div className="boxValue">{defaultTo(state.projectDataObj.description, "-")}</div>
          </Col>
        </Row>
      </div>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isDisabled={!state.isEdit}
          currentIcon={<EditOutlined className="icon-medium" />}
          clickCallback={editPreventiveForm}
        />
      </Row>
      <Collapse items={items} />

      {state.modal.visible && (
        <Modal
          width={700}
          title={<b>{t("projectEstimate.detail.tabs.costEstimation.modal.title")}</b>}
          open={state.modal.visible}
          onCancel={closeModal}
          footer={[
            <Button
              key="okButton"
              currentIcon={<SaveOutlined className="icon-medium" />}
              clickCallback={formikPreventiveForm.submitForm}
              isDisabled={!(formikPreventiveForm.dirty && formikPreventiveForm.isValid)}
            />,
          ]}
        >
          {state.loading && (
            <div className="container-spinner-positioning">
              <Spin spinning={state.loading} className="spinner-positioning" />
            </div>
          )}
          <Row gutter={[40, 48]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="vertical">
                  {t("projectEstimate.detail.tabs.preventiveForm.vertical")}*
                </label>
              </Row>
              <Row>
                <Select
                  className="w-100"
                  id="vertical"
                  name="vertical"
                  placeholder={"vertical"}
                  onChange={formikPreventiveForm.handleChange("verticalId")}
                  onBlur={formikPreventiveForm.handleBlur("verticalId")}
                  value={formikPreventiveForm.values.verticalId}
                  options={state.verticalsList}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="status">
                  {t("projectEstimate.modal.status")}*
                </label>
              </Row>
              <Row>
                <Select
                  className="w-100"
                  id="status"
                  name="status"
                  placeholder={"status"}
                  onChange={formikPreventiveForm.handleChange("projectEstimateStatusId")}
                  onBlur={formikPreventiveForm.handleBlur("projectEstimateStatusId")}
                  value={formikPreventiveForm.values.projectEstimateStatusId}
                  options={state.statusList}
                />
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="description">
                  {t("projectEstimate.modal.description")}*
                </label>
              </Row>
              <Row>
                <Input
                  required
                  id="description"
                  name="description"
                  onChange={formikPreventiveForm.handleChange}
                  onBlur={formikPreventiveForm.handleBlur}
                  value={formikPreventiveForm.values.description}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="currency">
                  {t("projectEstimate.modal.currency")}*
                </label>
              </Row>
              <Row>
                <Select
                  className="w-100"
                  id="currency"
                  name="currency"
                  placeholder={"currency"}
                  onChange={formikPreventiveForm.handleChange("currencyId")}
                  onBlur={formikPreventiveForm.handleBlur("currencyId")}
                  value={formikPreventiveForm.values.currencyId}
                  options={state.currenciesList}
                />
              </Row>
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
}
